import type { ReactNode } from 'react';
import { useIsMobile } from '@/shared/mobile';
import { cn } from '@/shared/ui/cn';
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/shared/ui/drawer';
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/shared/ui/sheet';

export interface PixSheetProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: ReactNode;
  readonly description?: ReactNode;
  readonly footer?: ReactNode;
  readonly children: ReactNode;
  /** Rótulo da região, para o teste e o leitor de tela achar o painel. */
  readonly slot?: string;
}

/** Os drawers do legado (`@uy3/web-components` `Drawer`, `anchor='right'`): à direita na web, bottom sheet no iPhone. */
export function PixSheet({
  open,
  onOpenChange,
  title,
  description,
  footer,
  children,
  slot,
}: PixSheetProps) {
  const isMobile = useIsMobile();
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange} repositionInputs={false}>
        <DrawerContent data-slot={slot ?? 'pix-sheet'} className="max-h-[92dvh]">
          <DrawerHeader>
            <DrawerTitle>{title}</DrawerTitle>
            <DrawerDescription className={cn(!description && 'sr-only')}>
              {description ?? title}
            </DrawerDescription>
          </DrawerHeader>
          <DrawerBody className="flex flex-col gap-4">{children}</DrawerBody>
          {footer ? (
            <DrawerFooter className="flex-row border-t border-border pt-3 [&>*]:flex-1">
              {footer}
            </DrawerFooter>
          ) : null}
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent data-slot={slot ?? 'pix-sheet'} size="sm">
        <SheetHeader>
          <SheetTitle>{title}</SheetTitle>
          <SheetDescription className={cn(!description && 'sr-only')}>
            {description ?? title}
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="flex flex-col gap-4">{children}</SheetBody>
        {footer ? <SheetFooter>{footer}</SheetFooter> : null}
      </SheetContent>
    </Sheet>
  );
}
